<script setup lang="ts">
  import { MpLoadingContainer, MpText } from '@borg/ui';

  const projectConfig = useProjectConfig();
  const articleId = useRoute().params.adviceId as string;

  const { data: article, pending, error, refresh } = await articleDetailsRepository.get(articleId);
  const { data: relatedArticles } = await relatedArticlesRepository.getMany(articleId);

  const { t } = useI18n();
  const pageTitle = computed(() => article.value?.title ?? t('ADVICE_PAGE.TITLE'));
  const pageDescription = computed(() => article.value?.intro ?? t('ADVICE_PAGE.DESCRIPTION'));
  const articleImageUrl = computed(() =>
    article.value?.imageUrl
      ? {
          url: article.value.imageUrl,
          width: 820,
          height: 430,
        }
      : undefined,
  );

  watchEffect(() => {
    usePageHead({
      pageName: pageTitle.value,
      description: pageDescription.value,
    });
  });

  useSeoMeta({
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogType: 'article',
    ogImage: articleImageUrl,
  });
</script>

<template>
  <MpLoadingContainer
    :is-loading="pending"
    :has-error="!!error"
    @reload="refresh"
  >
    <div
      v-if="article"
      class="article-container"
    >
      <main>
        <div class="article">
          <img
            :src="article.imageUrl"
            :alt="article.title"
          />
          <div>
            <MpText
              as="h6"
              regular
            >
              {{ article.supertitle }}
            </MpText>
            <MpText
              :class="{ 'article--title': !!article.createdAt }"
              as="h1"
            >
              {{ article.title }}
            </MpText>
            <MpText
              v-if="!!article.createdAt"
              :bold="false"
              shade="50"
              as="h6"
            >
              {{ $d(...localeDateTimeFormat(article.createdAt, 'dmy')) }}
            </MpText>
            <MpText as="h4">
              {{ article.intro }}
            </MpText>
            <div
              class="article__content"
              v-html="article.content ?? ''"
            />
          </div>
          <TagList :topics="article.topics" />
        </div>
        <div
          v-if="relatedArticles && relatedArticles.length > 0"
          class="related-articles"
        >
          <MpText as="h2">
            {{ $t(`mp_article.related`) }}
          </MpText>
          <ArticleList :articles="relatedArticles" />
        </div>
      </main>
      <aside>
        <div class="article__topics">
          <TopicsAndArticles :article-id="articleId" />
        </div>
        <RecommendedJobs
          v-if="projectConfig.features.recommendedJobsOnArticlePage"
          scenario="article-page"
        />
      </aside>
    </div>
  </MpLoadingContainer>
</template>

<style lang="scss">
  .article__content {
    img {
      max-width: 100%;
    }

    .highlight {
      text-align: center;
      border-radius: 10px;
      border: 1px solid #eee;
      background-color: #fdfdfd;
      padding: 20px 30px;
    }
  }
</style>

<style scoped lang="scss">
  .article-container {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-70);

    @include screen-md {
      flex-direction: row;
      main {
        width: 46rem;
      }
      aside {
        display: flex;
        flex-direction: column;
        width: 22rem;
        gap: var(--mp-space-70);
      }
    }
  }

  aside {
    height: 100%;

    @include dark-mode {
      border-color: var(--mp-color-grey-800);
    }
  }

  .article {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-70);
    padding-bottom: var(--mp-space-70);
    border-bottom: solid 1px var(--mp-color-grey-200);

    &__topics {
      padding: var(--mp-space-70);
      border: 1px solid var(--mp-color-grey-200);
      border-radius: 15px;
    }

    &--title {
      margin-bottom: 8px;
    }

    @include dark-mode {
      border-color: var(--mp-color-grey-800);
    }

    img {
      border-radius: 15px;
      width: 100%;
      height: auto;
      max-height: 28rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
    }

    &__tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--mp-space-30);
    }
  }

  .related-articles {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-50);
    padding-top: var(--mp-space-70);
  }
</style>
